import React, { useCallback, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';

import { Table as AntdTable, Button } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';
import type { ColumnsType } from 'antd/es/table';

import { formatDate } from 'helpers/date';
import { useCdfGetList } from 'v2/hooks/useCdfGetList';
import type { IFilterParams, ICDFList } from 'v2/services/cdfService/types';

import { useFiscalDocuments } from '../../context';

interface IProps {
  params: IFilterParams;
}

type ParentRow = ICDFList[0];
type ChildRow = ICDFList[0]['list'][0];

const Table: React.FC<IProps> = ({ params }) => {
  const { setSelectedData } = useFiscalDocuments();
  const { list, isLoading } = useCdfGetList(params);

  const dataSource = useMemo(() => (Array.isArray(list) ? list : []), [list]);

  const total = useMemo(() => {
    return dataSource.reduce((acc, it) => {
      if (!Array.isArray(it.list)) {
        return acc;
      }

      return it.list.reduce((subAcc, i) => subAcc + i.value, acc);
    }, 0);
  }, [dataSource]);

  const openDetails = useCallback(
    (data: ChildRow) => {
      setSelectedData(data);
    },
    [setSelectedData]
  );

  const columns = useMemo(
    (): ColumnsType<ParentRow> => [
      AntdTable.EXPAND_COLUMN,
      {
        title: 'Conteúdo',
        dataIndex: 'date',
        key: 'date'
      },
      {
        title: 'Registros',
        key: 'registers',
        align: 'right',
        width: 120,
        render: (_, row) => row.list.length
      },
      {
        title: 'Total',
        key: 'total',
        align: 'right',
        width: 160,
        render: (_, row) => <FormattedNumber value={row.list.reduce((acc, item) => acc + item.value, 0)} format='BRL' />
      }
    ],
    []
  );

  const subColumns = useMemo(
    (): ColumnsType<ChildRow> => [
      {
        title: 'Data',
        key: 'reference_date',
        render: (_, row) => formatDate(row.reference_date, 'dd/MM/yyyy')
      },
      {
        title: 'Código',
        dataIndex: 'id',
        key: 'id'
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status'
      },
      {
        title: 'Nome - Documento',
        dataIndex: 'display',
        key: 'display'
      },
      {
        title: 'Valor',
        key: 'value',
        align: 'right',
        width: 140,
        render: (_, row) => <FormattedNumber value={row.value} format='BRL' />
      },
      {
        title: '',
        key: 'actions',
        width: 50,
        align: 'right',
        render: (_, row) => (
          <Button
            shape='circle'
            type='text'
            icon={<Icon.Ellipsis  />}
            className='cursor-pointer border-0 bg-transparent p-0 text-right'
            onClick={e => {
              e.stopPropagation();
              openDetails(row);
            }}
          />
        )
      }
    ],
    [openDetails]
  );

  return (
    <div className='mt-4 overflow-hidden shadow-md'>
      <AntdTable<ParentRow>
        columns={columns}
        dataSource={dataSource}
        rowKey={(record, index) => `${record.date}-${index || 0}`}
        loading={isLoading}
        pagination={false}
        scroll={{ x: 1000 }}
        expandable={{
          expandRowByClick: true,
          rowExpandable: (record: ParentRow) => record.list.length > 0,
          expandedRowRender: (record: ParentRow) => (
            <AntdTable<ChildRow>
              columns={subColumns}
              dataSource={record.list}
              rowKey='id'
              pagination={false}
              locale={{ emptyText: 'Nenhum registro encontrado.' }}
            />
          )
        }}
        footer={() =>
          !isLoading ? (
            <div className='text-right text-[[0.9375rem] font-bold'>
              Total&nbsp;
              <FormattedNumber value={total} format='BRL' />
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default Table;
